import {
  useDeleteCodeReviewCommentMutation,
  useGetCodeReviewCommentsQuery,
  useSubmitCodeReviewCommentMutation,
  useUpdateCodeReviewCommentMutation,
} from '@gql/gql-types';
import { useCallback, useEffect, useState } from 'react';

import type { ReviewComment, ReviewCommentSide } from './types';

interface UseCodeReviewCommentsReturn {
  comments: ReviewComment[];
  handleAddComment: (
    filename: string,
    lineNumber: number,
    side: ReviewCommentSide,
    content: string,
  ) => Promise<void>;
  handleEditComment: (id: string, content: string) => Promise<void>;
  handleDeleteComment: (id: string) => Promise<void>;
  handleRetryComment: (id: string) => Promise<void>;
  errorSnackbar: string | null;
  clearError: () => void;
}

export function useCodeReviewComments(): UseCodeReviewCommentsReturn {
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  const { data: commentsData } = useGetCodeReviewCommentsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [submitComment] = useSubmitCodeReviewCommentMutation();
  const [updateComment] = useUpdateCodeReviewCommentMutation();
  const [deleteComment] = useDeleteCodeReviewCommentMutation();

  // Initialize comments from server on first load
  useEffect(() => {
    if (commentsData?.getCodeReviewComments && !hasLoadedInitial) {
      setComments(
        commentsData.getCodeReviewComments.map((c) => ({
          id: c.id,
          filename: c.filename,
          lineNumber: c.lineNumber,
          side: c.side as ReviewCommentSide,
          content: c.content,
          createdAt: new Date(c.createdAt),
          updatedAt: new Date(c.updatedAt),
        })),
      );
      setHasLoadedInitial(true);
    }
  }, [commentsData, hasLoadedInitial]);

  const clearError = useCallback(() => {
    setErrorSnackbar(null);
  }, []);

  const handleAddComment = useCallback(
    async (
      filename: string,
      lineNumber: number,
      side: ReviewCommentSide,
      content: string,
    ) => {
      const tempId = `temp-${Date.now()}`;
      const now = new Date();

      // Add optimistically
      const newComment: ReviewComment = {
        id: tempId,
        filename,
        lineNumber,
        side,
        content,
        createdAt: now,
        updatedAt: now,
        pending: true,
      };
      setComments((prev) => [...prev, newComment]);

      try {
        const result = await submitComment({
          variables: {
            input: { filename, lineNumber, side, content },
          },
        });

        // Replace temp comment with server version
        if (result.data?.submitCodeReviewComment) {
          const serverComment = result.data.submitCodeReviewComment;
          setComments((prev) =>
            prev.map((c) =>
              c.id === tempId
                ? {
                    id: serverComment.id,
                    filename: serverComment.filename,
                    lineNumber: serverComment.lineNumber,
                    side: serverComment.side as ReviewCommentSide,
                    content: serverComment.content,
                    createdAt: new Date(serverComment.createdAt),
                    updatedAt: new Date(serverComment.updatedAt),
                  }
                : c,
            ),
          );
        } else {
          // No data but no error - just mark as not pending
          setComments((prev) =>
            prev.map((c) => (c.id === tempId ? { ...c, pending: false } : c)),
          );
        }
      } catch {
        setComments((prev) =>
          prev.map((c) =>
            c.id === tempId ? { ...c, pending: false, error: true } : c,
          ),
        );
        setErrorSnackbar('Failed to save comment. Click retry to try again.');
      }
    },
    [submitComment],
  );

  const handleEditComment = useCallback(
    async (id: string, newContent: string) => {
      // Optimistically update
      setComments((prev) =>
        prev.map((c) =>
          c.id === id
            ? { ...c, content: newContent, updatedAt: new Date() }
            : c,
        ),
      );

      try {
        await updateComment({
          variables: {
            input: { id, content: newContent },
          },
        });
      } catch {
        setErrorSnackbar('Failed to update comment.');
      }
    },
    [updateComment],
  );

  const handleDeleteComment = useCallback(
    async (id: string) => {
      // Store comment for potential rollback
      const deletedComment = comments.find((c) => c.id === id);

      // Optimistically remove
      setComments((prev) => prev.filter((c) => c.id !== id));

      // Only call server if it's not a temp ID
      if (!id.startsWith('temp-')) {
        try {
          await deleteComment({
            variables: {
              input: { id },
            },
          });
        } catch {
          // Rollback: restore the deleted comment
          if (deletedComment) {
            setComments((prev) => [...prev, deletedComment]);
          }
          setErrorSnackbar('Failed to delete comment.');
        }
      }
    },
    [comments, deleteComment],
  );

  const handleRetryComment = useCallback(
    async (id: string) => {
      const failedComment = comments.find((c) => c.id === id && c.error);
      if (!failedComment) return;

      // Mark as pending
      setComments((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, pending: true, error: false } : c,
        ),
      );

      try {
        const result = await submitComment({
          variables: {
            input: {
              filename: failedComment.filename,
              lineNumber: failedComment.lineNumber,
              side: failedComment.side,
              content: failedComment.content,
            },
          },
        });

        if (result.data?.submitCodeReviewComment) {
          const serverComment = result.data.submitCodeReviewComment;
          setComments((prev) =>
            prev.map((c) =>
              c.id === id
                ? {
                    id: serverComment.id,
                    filename: serverComment.filename,
                    lineNumber: serverComment.lineNumber,
                    side: serverComment.side as ReviewCommentSide,
                    content: serverComment.content,
                    createdAt: new Date(serverComment.createdAt),
                    updatedAt: new Date(serverComment.updatedAt),
                  }
                : c,
            ),
          );
        } else {
          // No data but no error - just mark as not pending
          setComments((prev) =>
            prev.map((c) => (c.id === id ? { ...c, pending: false } : c)),
          );
        }
      } catch {
        setComments((prev) =>
          prev.map((c) =>
            c.id === id ? { ...c, pending: false, error: true } : c,
          ),
        );
        setErrorSnackbar('Failed to save comment. Click retry to try again.');
      }
    },
    [comments, submitComment],
  );

  return {
    comments,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    handleRetryComment,
    errorSnackbar,
    clearError,
  };
}
